import { Injectable } from '@angular/core';
import { AlertController } from '@ionic/angular';
import * as moment from 'moment';
import { BehaviorSubject, forkJoin, from, Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { ApiService } from 'src/app/core/api/api.service';
import { AuthService } from 'src/app/core/authentication/services/auth.service';
import { endpoints } from 'src/app/core/config/endpoints';
import { ConnectionService } from 'src/app/core/connection/connection.service';
import { ConfigSyncResponse, DynamicSyncResponse } from 'src/app/core/models/sync.model';
import { RequestQueueService } from 'src/app/core/services/requestQueue/request-queue.service';
import { LocalStorageService } from 'src/app/core/storage/local-storage/local-storage.service';
import { LoggerService } from 'src/app/core/logger/logger.service';

@Injectable({
	providedIn: 'root'
})
export class SyncService {
	isSyncing$ = new BehaviorSubject<boolean>(null);
	isBlockingSync$ = new BehaviorSubject<boolean>(null);
	public lastSyncTime: BehaviorSubject<string> = new BehaviorSubject(null);

	constructor(
		private api: ApiService,
		private storage: LocalStorageService,
		private connectionService: ConnectionService,
		private requestQueueService: RequestQueueService,
		private alertController: AlertController,
		private authService: AuthService,
		private loggerService: LoggerService
	) {}

	async sync(disableFullSyncPrompt = false): Promise<void> {
		if (this.connectionService.isOnline$.value && !this.isSyncing$.value && await this.authService.isLoggedIn()) {
			if (await this.requestQueueService.hasFailedRequests()) {
				this.requestQueueService.replayFailedRequests();
				this.toggleSyncingFlags(false);
				return;
			}

			if (await this.hasMissingConfig()) {
				this.performSync(true, disableFullSyncPrompt);
				return;
			}

			if (await this.storage.hasNewIDBVersion()) {
				this.performSync(true);
				return;
			}

			this.getUpdateBit()
				.subscribe((needsConfig: boolean) => this.performSync(needsConfig, disableFullSyncPrompt));
		}
	}

	private async performSync(performFullSync: boolean, disableFullSyncPrompt = false): Promise<void> {
		this.isSyncing$.next(true);
		const syncRequests = [this.dynamicSync()];

		if (performFullSync) {
			if (!disableFullSyncPrompt) {
				performFullSync = (await this.promptForFullSync()).data;
			}

			if (performFullSync) {
				this.isBlockingSync$.next(true);
				syncRequests.push(this.configSync());
			}
		}

		// Wait for both requests to complete
		forkJoin(syncRequests).subscribe(
			async () => {
				if (performFullSync) {
					await this.updateIDB();

					// Force reload after configuration sync
					window.location.reload();
				}

				this.toggleSyncingFlags(false);
			},
			() => this.toggleSyncingFlags(false)
		);
	}

	async updateIDB(): Promise<void> {
		if (await this.storage.hasNewIDBVersion()) {
			await this.storage.updateLastIDBVersion();
		}
	}

	toggleSyncingFlags(syncing: boolean): void {
		this.isSyncing$.next(syncing);
		this.isBlockingSync$.next(syncing);
	}

	dynamicSync(): Observable<void> {
		let endpoint = endpoints.sync.dynamic;
		if (this.dateStrings != null) {
			endpoint += '?apptDatesString=' + this.dateStrings;
        }
		return this.api.get<DynamicSyncResponse>(endpoint)
			.pipe(
				mergeMap(response => from(this.storeDynamicSync(response))),
				mergeMap(_ => from(this.updateLastSyncTime()))
			);
	}

	storeDynamicSync(response: DynamicSyncResponse): Promise<any> {
		response.appointments.map(
			appointment => {
				appointment.dataTab = JSON.parse(appointment.dataTab as string);
				return appointment;
			}
		);

		return Promise.all([
			this.storage.appointments.bulkSet(response.appointments),
			this.storage.timeEntries.bulkSet(response.timeEntries, 'apptId'),
			this.storage.truckInventory.bulkSet(response.truckInventory, 'id'),
		]);
	}

	configSync(): Observable<any> {
		return this.api.get<ConfigSyncResponse>(endpoints.sync.config)
			.pipe(
				mergeMap(response => from(this.storeConfigSync(response))),
				mergeMap(_ => from(this.resetUpdateBit())),
				tap(() => this.clearUserData()),
			);
	}

	async clearUserData(): Promise<void> {
		await this.storage.appointmentClearCode.clear();
	}

	storeConfigSync(response: ConfigSyncResponse): Promise<any> {
		return Promise.all([
			this.storage.configSettings.bulkSet(response.configSettings, 'setting'),
			this.storage.equipmentAttributes.bulkSet(response.equipmentAttributes, 'id'),
			this.storage.equipmentCategories.bulkSet(response.equipmentCategories, 'id'),
			this.storage.inventoryStatuses.bulkSet(response.inventoryStatuses, 'id'),
			this.storage.inventoryRemovalReasons.bulkSet(response.inventoryRemovalReasons, 'id'),
			this.storage.ticketStatusTransitions.bulkSet(response.ticketStatusTransitions),
			this.storage.ticketClearCodeTypes.bulkSet(response.ticketClearCodeTypes, 'codeId'),
			this.storage.ticketClearCodeOptions.bulkSet(response.ticketClearCodeOptions, 'cctId'),
			this.storage.ticketClearCodeAllowed.bulkSet(response.ticketClearCodeAllowed, 'tcId'),
			this.storage.additionalCharges.bulkSet(response.additionalCharges, 'id'),
			this.storage.payCodes.bulkSet(response.payCodes, 'userId'),
			this.storage.timeCodes.bulkSet(response.timeCodes, 'userId'),
			this.storage.forms.bulkSet(response.forms, 'id'),
		]);
	}

	resetUpdateBit(): Promise<void> {
		return this.api.post(endpoints.sync.resetUpdateBit, null).toPromise()
			.then(_ => console.log('Successfully reset update bit'));
	}

	async updateLastSyncTime(): Promise<void> {
		const url = endpoints.sync.updateLastSyncTime;
		const lastSyncTime = moment().toISOString(true);
		const patchData = { lastSyncTime };

		// Update indexeddb table
		await this.storage.sync.setItem('lastSyncTime', lastSyncTime);

		// Update server
		this.api.patch(url, patchData).subscribe();

		this.lastSyncTime.next(lastSyncTime);
	}

	async hasMissingConfig(): Promise<boolean> {
		const transitions = await this.storage.ticketStatusTransitions.length();

		return (transitions === 0);
	}

	async needsFullSync(): Promise<boolean> {
		return await this.hasMissingConfig() || await this.getUpdateBit().toPromise();
	}

	getUpdateBit(): Observable<boolean> {
		return this.api.get(endpoints.sync.getUpdateBit);
	}

	private async promptForFullSync(): Promise<any> {
		const syncAlert = await this.alertController.create({
			header: 'Full Sync Needed',
			message: 'A change in configuration data has been detected. A full sync will need to be performed. ' +
				'You may lose any recent changes that have not been synced. Would you like to continue with a full sync?',
			backdropDismiss: false,
			buttons: [
				{
					text: 'NO',
					handler: () => {
						syncAlert.dismiss(false);
						return false;
					}
				},
				{
					text: 'YES',
					handler: () => {
						syncAlert.dismiss(true);
						return false;
					}
				},
			],
		});

		await syncAlert.present();
		return await syncAlert.onDidDismiss();
	}

	async reloadConfiguration() {
		const reloadConfigAlert = await this.alertController.create({
			header: 'Reload Configuration',
			message: 'Are you sure you would like to reload configuration? Any unsaved changes may be lost.',
			backdropDismiss: false,
			buttons: [
				{
					text: 'Cancel',
					role: 'cancel',
				},
				{
					text: 'Reload',
					handler: async () => {
						await this.loggerService.logTrace('Reloading configuration');
						await this.requestQueueService.clearFailedRequests();
						await this.storage.pendingTicketProvisions.clear();
						await this.performSync(true, true);
					},
				},
			]
		});

		await reloadConfigAlert.present();
	}

	// LOC-1086
	private dateStrings: string[] = [];
	pushDateString(dateString: string): void {
		let date = new Date(dateString);
		dateString = date.toISOString();
		if (!this.dateStrings.includes(dateString)) {
			this.dateStrings.push(dateString);
		}

    }
}
